import React, { useMemo } from 'react';
import { RichTextBlock } from 'prismic-reactjs';
import { ImageProps } from '@components/image';
import Text from '@components/text';
import getLinkConfig from '@helpers/get-link-config';
import Wrapper from '@components/wrapper.styled';
import AnimatedElement from '@components/animated-element';
import {
  Content,
  Header,
  LinkButton,
  StyledImage,
  TextContent,
  StyledWrapper,
  ImageWrapper,
  RightColumn,
  LeftColumn,
} from './photography-hero.styled';

export interface LinkProps {
  url?: string;
  uid?: string;
  target?: string;
}

export interface PhotographyHeroProps {
  image?: ImageProps;
  backgroundImage?: ImageProps;
  title?: string;
  subheading?: RichTextBlock[];
  linkLabel?: string;
  link?: LinkProps;
}

const DEFAULT_LINK_LABEL = 'Request a Demo';

const PhotographyHero: React.FC<PhotographyHeroProps> = ({
  image,
  backgroundImage,
  title,
  subheading = [],
  linkLabel,
  link,
}) => {
  if (!title && !image && !subheading.length) {
    return null;
  }

  const linkConfig = useMemo(() => getLinkConfig(link), [link]);
  const hasImage = useMemo(() => !!(image?.gatsbyImageData || image?.url), [image]);

  return (
    <Header
      className="page-hero hero-primary"
      role="banner"
      aria-label={title || 'Page Hero'}
      backgroundImage={backgroundImage}
    >
      <Wrapper>
        <StyledWrapper>
          <Content image={image}>
            <TextContent>
              <AnimatedElement
                animationType="slideInLeft"
                delay={0}
                threshold={0.1}
                rootMargin="50px"
                className="animate-wrapper"
              >
                <LeftColumn>
                  {title && (
                    <Text
                      fontSize="heading2"
                      type="title"
                      tag="h1"
                      stringText={title}
                      fontColor="white"
                      margin="none"
                    />
                  )}
                </LeftColumn>
              </AnimatedElement>
              <AnimatedElement
                animationType="slideInRight"
                delay={0}
                threshold={0.1}
                rootMargin="50px"
                className="animate-wrapper"
              >
                <RightColumn>
                  {subheading.length > 0 && (
                    <Text
                      type="description"
                      richText={subheading}
                      fontColor="white"
                      margin="none"
                    />
                  )}
                  {linkConfig.url !== '' && (
                    <LinkButton
                      type="button"
                      url={linkConfig.url}
                      title={linkLabel || DEFAULT_LINK_LABEL}
                      isExternal={linkConfig.isExternal}
                      target={link?.target || '_self'}
                      aria-label={linkLabel || DEFAULT_LINK_LABEL}
                    />
                  )}
                </RightColumn>
              </AnimatedElement>
            </TextContent>
          </Content>
        </StyledWrapper>
      </Wrapper>
      {hasImage && (
        <AnimatedElement
          animationType="slideUp"
          delay={0}
          threshold={0.1}
          rootMargin="50px"
          className="animate-wrapper"
        >
          <ImageWrapper>
            <StyledImage
              gatsbyImageData={image?.gatsbyImageData}
              url={image?.url}
              alt={image?.alt || title || 'Hero image'}
              objectFit="cover"
              loading="eager"
            />
          </ImageWrapper>
        </AnimatedElement>
      )}
    </Header>
  );
};

PhotographyHero.defaultProps = {
  subheading: [],
};

export default React.memo(PhotographyHero);
